<template>
  <div class="room-booking-page">
    <!-- (2) 中間Banner或輪播 -->
    <div v-if="activeTab === 'booking'">
      <!-- (1) 頂部按鈕區 -->
      <div class="top-buttons">
        <button
          class="title-button"
          :class="{ active: activeTab === 'booking' }"
          @click="switchTab('booking')"
        >
          訂房預約
        </button>
        <button
          class="title-button"
          :class="{ active: activeTab === 'bathhouse' }"
          @click="switchTab('bathhouse')"
        >
          湯屋預約
        </button>
      </div>

      <!-- (3) 日期選擇+房客彈窗(含房間數) -->
      <div class="date-section">
        <!-- 入住日期 -->
        <div class="date-field">
          <label for="checkin-date">入住日期</label>
          <input
            id="checkin-date"
            type="date"
            v-model="checkinDate"
            :min="minDate"
            @change="
              () => {
                validateDates();
                fetchAvailableRooms();
              }
            "
          />
        </div>
        <!-- 退房日期 -->
        <div class="date-field">
          <label for="checkout-date">退房日期</label>
          <input
            id="checkout-date"
            type="date"
            v-model="checkoutDate"
            :min="checkoutMinDate"
            @change="
              () => {
                validateDates();
                fetchAvailableRooms();
              }
            "
          />
        </div>

        <div class="date-field guest-field" @click="toggleRoomGuestDropdown">
          <label for="guest-select">房間 & 人數</label>
          <div class="guest-select-box">
            <span id="guest-select">
              {{ roomCount }} 間房, {{ roomAdultCount }} 位大人,
              {{ roomChildCount }} 位兒童, {{ roomInfantCount }} 位嬰兒
            </span>
          </div>
        </div>

        <!-- 人數選擇彈窗 -->
        <div class="guest-dropdown" v-if="isRoomGuestDropdownVisible">
          <div class="guest-config">
            <!-- 房間數選擇 -->
            <div class="guest-row">
              <label>房間數</label>
              <div class="counter">
                <button @click="decrementRoomCount" :disabled="roomCount <= 1">
                  -
                </button>
                <span>{{ roomCount }}</span>
                <button @click="incrementRoomCount" :disabled="roomCount >= 15">
                  +
                </button>
              </div>
            </div>
            <!-- 成人數選擇 -->
            <div class="guest-row">
              <label>成人</label>
              <div class="counter">
                <button
                  @click="decrementRoomAdult"
                  :disabled="roomAdultCount <= 1"
                >
                  -
                </button>
                <span>{{ roomAdultCount }}</span>
                <button
                  @click="incrementRoomAdult"
                  :disabled="roomAdultCount >= 70"
                >
                  +
                </button>
              </div>
            </div>
            <!-- 兒童數選擇 -->
            <div class="guest-row">
              <label>兒童</label>
              <div class="counter">
                <button @click="decrementRoomChild">-</button>
                <span>{{ roomChildCount }}</span>
                <button @click="incrementRoomChild">+</button>
              </div>
            </div>
            <!-- 嬰兒數選擇 -->
            <div class="guest-row">
              <label>嬰兒</label>
              <div class="counter">
                <button @click="decrementRoomInfant">-</button>
                <span>{{ roomInfantCount }}</span>
                <button @click="incrementRoomInfant">+</button>
              </div>
            </div>
          </div>

          <!-- 彈窗底部操作 -->
          <div class="dropdown-actions">
            <button @click="closeRoomGuestDropdown">完成</button>
          </div>
        </div>
      </div>

      <!-- 兩欄結構：左邊(所有房型) + 右邊(固定預定資訊) -->
      <div class="main-content">
        <!-- 左欄：放所有房型區塊 -->
        <div v-if="isLoading" class="loading-message">正在搜尋可用房型...</div>
        <div class="rooms-section">
          <div v-for="room in rooms" :key="room.id" class="room-block">
            <!-- 動態顯示房型圖片 -->
            <div
              class="room-img-area1"
              @click="openVerticalImageModal(getRoomImage(room))"
            >
              <img
                :src="getRoomImage(room)"
                :alt="room.name + '房間圖片'"
                @error="handleImageError($event, room)"
              />
            </div>

            <!-- 動態顯示房型資訊 -->
            <div class="room-info-area">
              <div class="room-info">
                <h2>{{ room.name }}</h2>
                <p>可入住人數: {{ room.capacity }}</p>
                <p>房間數：{{ room.stock }}間房</p>
                <p>{{ room.description }}</p>
                <!-- 點擊後呼叫openMoreInfoModal，帶入此房型的名稱 -->
              </div>

              <!-- 動態專案列表 -->
              <div class="package-list">
                <!-- 固定專案 -->
                <div class="package-item">
                  <div class="package-left">
                    <h3>純住宿 - 不含早餐</h3>
                  </div>
                  <div class="package-right">
                    <div class="price">
                      NT$
                      {{ (room.pricePerNight * stayNights).toLocaleString() }}
                    </div>

                    <p>{{ room.capacity }}位賓客 {{ stayNights }}晚的費用</p>
                    <button
                      class="select-btn"
                      @click="
                        addToCart(
                          room,
                          '純住宿 - 不含早餐',
                          room.pricePerNight * stayNights,
                          null
                        )
                      "
                    >
                      選擇
                    </button>
                  </div>
                </div>

                <!-- 動態活動專案 -->
                <div
                  v-for="packageItem in room.packages.filter(
                    (p) => p.id !== null
                  )"
                  :key="packageItem.id"
                  class="package-item"
                >
                  <div class="package-left">
                    <h3>{{ packageItem.name }}</h3>
                  </div>

                  <div class="package-right">
                    <div class="price">
                      NT$
                      {{
                        (packageItem.finalPrice * stayNights).toLocaleString()
                      }}
                    </div>
                    <p>{{ room.capacity }}位賓客 {{ stayNights }}晚的費用</p>
                    <button
                      class="select-btn"
                      @click="
                        addToCart(
                          room,
                          packageItem.name,
                          packageItem.finalPrice * stayNights,
                          packageItem.id
                        )
                      "
                    >
                      選擇
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal -->
        <div class="modal-overlay" v-if="showModal">
          <div class="modal-content">
            <!-- 動態顯示所點擊房型的詳細內容 -->
            <h3>溫馨提醒</h3>
            <ul>
              <li>軟, 硬式睡枕 (需預約), 羽絨枕, 羽絨被</li>
              <li>兒童備品, 消毒鍋, 嬰兒床, 嬰兒浴盆 (需預約)</li>
              <li>Mini Bar: 免費瓶裝水, 濾掛咖啡, 茶葉包</li>
            </ul>
            <!-- 關閉按鈕 -->
            <button class="close-modal-btn-1" @click="closeMoreInfoModal">
              關閉
            </button>
          </div>
        </div>

        <!-- 確保 @click 事件正確綁定 -->
        <div class="summary-section" :class="{ expanded: isSummaryExpanded }">
          <button class="summary-tab" @click="toggleSummary">預訂資訊</button>
          <div class="summary-content">
            <h3>🏨 您的預訂</h3>
            <p>
              <span>入住:</span> <strong>{{ checkinDate }}</strong>
            </p>
            <p>
              <span>退房:</span> <strong>{{ checkoutDate }}</strong>
            </p>
            <p>
              <span>房間數:</span> <strong>{{ roomCount }}</strong>
            </p>
            <p>
              <span>總人數:</span> <strong>{{ totalGuests }}</strong>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- 以下為湯屋預約 -->

    <div v-else-if="activeTab === 'bathhouse'">
      <!-- 湯屋預定內容 -->

      <!-- 切換按鈕區 -->
      <div class="spring-top-buttons">
        <button
          class="spring-title-button"
          :class="{ active: activeTab === 'booking' }"
          @click="switchTab('booking')"
        >
          訂房預約
        </button>
        <button
          class="spring-title-button"
          :class="{ active: activeTab === 'bathhouse' }"
          @click="switchTab('bathhouse')"
        >
          湯屋預約
        </button>
      </div>

      <div class="date-section">
        <!-- 日期選擇 -->
        <div class="spring-date-field">
          <label for="bathhouse-date">使用日期</label>
          <input id="bathhouse-date" type="date" v-model="bathhouseDate" />
        </div>
        <div class="spring-date-field">
          <label for="time-slot">使用時段</label>
          <select id="time-slot" v-model="timeSlot">
            <option value="morning">上午 10:00~12:00</option>
            <option value="afternoon">下午 13:00~15:00</option>
            <option value="evening">晚上 18:00~20:00</option>
          </select>
        </div>
        <div
          class="spring-date-field guest-field"
          @click="toggleBathhouseGuestDropdown"
        >
          <label for="guest-select">人數</label>
          <div class="guest-select-box">
            <span id="guest-select">
              {{ bathhouseAdultCount }} 位大人, {{ bathhouseChildCount }} 位兒童
            </span>
          </div>
        </div>

        <!-- 人數選擇彈窗 -->
        <div
          class="spring-guest-dropdown"
          v-if="isBathhouseGuestDropdownVisible"
        >
          <div class="spring-guest-config">
            <!-- 成人數選擇 -->
            <div class="spring-guest-row">
              <label>成人</label>
              <div class="spring-counter">
                <button @click="decrementBathhouseAdult">-</button>
                <span>{{ bathhouseAdultCount }}</span>
                <button @click="incrementBathhouseAdult">+</button>
              </div>
            </div>
            <!-- 兒童數選擇 -->
            <div class="spring-guest-row">
              <label>兒童</label>
              <div class="spring-counter">
                <button @click="decrementBathhouseChild">-</button>
                <span>{{ bathhouseChildCount }}</span>
                <button @click="incrementBathhouseChild">+</button>
              </div>
            </div>
          </div>

          <!-- 彈窗底部操作 -->
          <div class="dropdown-actions">
            <button @click="closeBathhouseGuestDropdown">完成</button>
          </div>
        </div>
      </div>

      <div class="main-content">
        <!-- 湯屋內容 -->
        <div class="spring-section">
          <!-- 靜湯閣 -->
          <div class="spring-block">
            <div class="spring-img-area">
              <div
                class="spring-img"
                @click="
                  openImageModal(require('@/assets/picture/spring-1.png'))
                "
              >
                <img
                  :src="require('@/assets/picture/spring-1.png')"
                  alt="靜湯閣湯屋圖片"
                />
              </div>
            </div>
            <div class="spring-info-area">
              <h2>靜湯閣</h2>
              <p>適合 2 人，優雅的現代設計，享受靜謐的湯屋體驗。</p>
              <a
                href="#"
                class="spring-more-info"
                @click.prevent="openMoreInfoModal('靜湯閣')"
                ></a
              >
              <button class="select-spring-btn" @click="selectRoom('靜湯閣')">
                選擇
              </button>
            </div>
          </div>

          <!-- 泉韻居 -->
          <div class="spring-block">
            <div class="spring-img-area">
              <div
                class="spring-img"
                @click="
                  openImageModal(require('@/assets/picture/spring-2.png'))
                "
              >
                <img
                  :src="require('@/assets/picture/spring-2.png')"
                  alt="泉韻居湯屋圖片"
                />
              </div>
            </div>
            <div class="spring-info-area">
              <h2>泉韻居</h2>
              <p>適合家庭或 4 人，經典傳統設計，享受溫泉文化的洗禮。</p>
              <a
                href="#"
                class="spring-more-info"
                @click.prevent="openMoreInfoModal('泉韻居')"
                ></a
              >
              <button class="select-spring-btn" @click="selectRoom('泉韻居')">
                選擇
              </button>
            </div>
          </div>

          <!-- 湯之苑 -->
          <div class="spring-block">
            <div class="spring-img-area">
              <div
                class="spring-img"
                @click="
                  openImageModal(require('@/assets/picture/spring-3.png'))
                "
              >
                <img
                  :src="require('@/assets/picture/spring-3.png')"
                  alt="湯之苑湯屋圖片"
                />
              </div>
            </div>
            <div class="spring-info-area">
              <h2>湯之苑</h2>
              <p>適合團體，最多 6 人，擁有寬敞空間和豪華設施。</p>
              <a
                href="#"
                class="spring-more-info"
                @click.prevent="openMoreInfoModal('湯之苑')"
                ></a
              >
              <button class="select-spring-btn" @click="selectRoom('湯之苑')">
                選擇
              </button>
            </div>
          </div>
        </div>

        <!-- 圖片放大彈窗 -->
        <div
          v-if="showImageModal"
          class="image-modal-overlay"
          @click="closeImageModal"
        >
          <div class="image-modal-content" @click.stop>
            <img :src="currentImage" alt="放大圖片" />
            <button class="close-modal-btn" @click="closeImageModal">
              關閉
            </button>
          </div>
        </div>

        <!-- ✅ 湯屋預約資訊可展開區塊 -->
<div class="summary-section" :class="{ expanded: isSummaryExpanded }">
  <button class="summary-tab" @click="toggleSummary">預訂資訊</button> <!-- 點擊切換展開 -->
  <div class="summary-content">
    <h3>🛁 湯屋預約</h3>
    <p><span>使用日期:</span> <strong>{{ bathhouseDate }}</strong></p> <!-- 顯示使用日期 -->
    <p><span>時段:</span> <strong>{{ formattedTimeSlot }}</strong></p> <!-- 顯示時段 -->
    <p><span>大人數:</span> <strong>{{ bathhouseAdultCount }}</strong></p> <!-- 顯示大人數 -->
    <p><span>兒童數:</span> <strong>{{ bathhouseChildCount }}</strong></p> <!-- 顯示兒童數 -->
  </div>
</div>
      </div>

      <!-- Modal -->
      <div
        class="spring-modal-overlay"
        v-if="showModal && activeTab === 'bathhouse'"
      >
        <div class="spring-modal-content">
          <h3>{{ currentRoomTitle }} - 更完整資訊</h3>
          <p v-if="currentRoomTitle === '湯屋A'">
            這裡可放置「湯屋A」的詳細描述。
          </p>
          <p v-else-if="currentRoomTitle === '靜湯閣'">
            靜湯閣提供精緻的現代湯屋設計，特別適合情侶或 2 人的小型聚會。
          </p>
          <p v-else-if="currentRoomTitle === '泉韻居'">
            泉韻居傳承溫泉文化的典雅設計，擁有適合 4
            人的寬敞空間，是家庭旅遊的首選。
          </p>
          <p v-else-if="currentRoomTitle === '湯之苑'">
            湯之苑以豪華設計和寬敞空間為特色，最多可容納 6
            人，適合朋友聚會或小型團體。
          </p>
          <p v-else>尚未選擇房型或詳細資訊尚未撰寫。</p>
          <button @click="closeMoreInfoModal">關閉</button>
        </div>
      </div>
    </div>

    <div
      v-if="showImageModal"
      class="image-modal-overlay"
      @click="closeImageModal"
    >
      <div class="image-modal-content" @click.stop>
        <img :src="currentImage" alt="放大圖片" />
        <button class="close-modal-btn" @click="closeImageModal">關閉</button>
      </div>
    </div>

    <!-- Cart -->
    <button class="floating-booking-btn" @click="navigateToCart">
      <img src="@/assets/picture/cart.png" alt="Cart" class="btn-icon" />
      購物車
      <div
        v-if="cartItemCount > 0"
        style="
          position: absolute;
          top: -5px;
          right: -5px;
          background-color: red;
          color: white;
          font-size: 14px;
          font-weight: bold;
          border-radius: 50%;
          width: 22px;
          height: 22px;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1100;
        "
      >
        {{ cartItemCount }}
      </div>
    </button>

    <!-- Vertical Image Modal -->
    <div
      v-if="showVerticalImageModal"
      class="vertical-image-modal-overlay"
      @click="closeVerticalImageModal"
    >
      <div class="vertical-image-modal-content" @click.stop>
        <img :src="currentVerticalImage" alt="放大圖片" />
        <button
          class="vertical-close-modal-btn"
          @click="closeVerticalImageModal"
        >
          關閉
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import bookingRoom from "@/assets/js/booking-room.js"; // 混入房間邏輯
import bookingSpa from "@/assets/js/booking-spa.js"; // 混入湯屋邏輯
import cartApi from "@/assets/js/cart.js"; // 購物車 API
import Swal from "sweetalert2"; // 引入 SweetAlert2
import { searchAvailableRooms } from "@/assets/js/roomtype.js"; // 查詢可用房型方法

export default {
  name: "BookingPage",
  mixins: [bookingRoom, bookingSpa], // 混入邏輯模組

  data() {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const formatDate = (d) => d.toISOString().split("T")[0];

    return {
      // UI 控制相關
      activeTab: "booking", // 當前選擇的頁籤
      showImageModal: false, // 是否顯示圖片彈窗
      showVerticalImageModal: false, // 是否顯示直向圖彈窗
      currentRoomTitle: "", // 當前顯示房型名稱
      currentImage: "", // 圖片彈窗用的圖片
      currentVerticalImage: "", // 直向圖片用的路徑

      // 房型與預約資料
      rooms: [], // 房型清單
      roomPackages: {}, // 房型對應套餐
      checkinDate: formatDate(today), // 預設入住日期
      checkoutDate: formatDate(tomorrow), // 預設退房日期
      bathhouseDate: "", // 湯屋日期
      timeSlot: "morning", // 湯屋時段
      isLoading: false, // 載入狀態
      errorMessage: "", // 錯誤訊息顯示用

      // 房間人數
      roomCount: 1,
      roomAdultCount: 2,
      roomChildCount: 0,
      roomInfantCount: 0,

      isSummaryExpanded: false, // 抽屜側滑狀態
       isSpringSummaryExpanded: false, // 湯屋預約區塊
      showModal: false, // ✅ 補上這一行
    };
  },

  computed: {
    // 顯示湯屋時段文字
    formattedTimeSlot() {
      const timeSlotMap = {
        morning: "上午 10:00~12:00",
        afternoon: "下午 13:00~15:00",
        evening: "晚上 18:00~20:00",
      };
      return timeSlotMap[this.timeSlot] || "未選擇";
    },

    // 計算入住天數
    stayNights() {
      if (this.checkinDate && this.checkoutDate) {
        const checkin = new Date(this.checkinDate);
        const checkout = new Date(this.checkoutDate);
        const diffTime = checkout - checkin;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // 天數
      }
      return 0;
    },

    // 房間總人數
    totalRoomGuests() {
      return this.roomAdultCount + this.roomChildCount + this.roomInfantCount;
    },

    // 湯屋總人數
    totalBathhouseGuests() {
      return this.bathhouseAdultCount + this.bathhouseChildCount;
    },

    // 總人數（房間 + 湯屋）
    totalGuests() {
      return this.totalRoomGuests + this.totalBathhouseGuests;
    },

    // 最小日期（今天）
    minDate() {
      const today = new Date();
      return today.toISOString().split("T")[0];
    },

    // 退房最小日期 = 入住 + 1 天
    checkoutMinDate() {
      if (this.checkinDate) {
        const checkin = new Date(this.checkinDate);
        checkin.setDate(checkin.getDate() + 1);
        return checkin.toISOString().split("T")[0];
      }
      return this.minDate;
    },

    // 提供 API 所需人數
    totalGuestsForAPI() {
      return this.roomAdultCount + this.roomChildCount;
    },

    cartItemCount() {
      // ✅ 這裡根據實際情況調整，例如從 localStorage 拿或 props 傳入
      const cart = JSON.parse(localStorage.getItem("cart")) || [];
      return cart.length;
    },
  },

  methods: {
    // 加入購物車功能
    async addToCart(room, packageName, totalPrice, packageId) {
      try {
        await cartApi.addToCart(
          room,
          packageName,
          totalPrice,
          packageId,
          this.checkinDate,
          this.checkoutDate
        );
      } catch (err) {
        console.error("加入購物車錯誤", err);
      }
    },

    // 切換頁籤
    switchTab(tabName) {
      this.activeTab = tabName;
    },

    // 開啟圖片彈窗
    openImageModal(imagePath) {
      this.currentImage = imagePath;
      this.showImageModal = true;
    },

    // 關閉圖片彈窗
    closeImageModal() {
      this.showImageModal = false;
    },

    // 開啟直向圖片彈窗
    openVerticalImageModal(imagePath) {
      this.currentVerticalImage = imagePath;
      this.showVerticalImageModal = true;
    },

    // 關閉直向圖片彈窗
    closeVerticalImageModal() {
      this.showVerticalImageModal = false;
    },

    // 查詢房型
    async fetchAvailableRooms() {
      if (!this.isDateValid) {
        console.log("日期無效，跳過查詢");
        return;
      }

      try {
        const requestPayload = {
          CheckInDate: this.checkinDate,
          CheckOutDate: this.checkoutDate,
          RequiredRoomCount: this.roomCount,
          RequiredPeopleCount: this.totalGuestsForAPI,
          ServiceType: this.activeTab === "booking" ? "房間" : "湯屋",
        };

        const availableRooms = await searchAvailableRooms(requestPayload);
        this.rooms = availableRooms;
      } catch (error) {
        console.error("無法獲取可用房型:", error);
        this.rooms = [];
        Swal.fire({
          icon: "error",
          title: "查詢房型失敗",
          text: "請檢查網路連線或稍後再試",
        });
      }
    },

    // 驗證日期正確性
    validateDates() {
      const today = new Date().toISOString().split("T")[0];

      if (this.checkinDate && this.checkinDate < today) {
        Swal.fire({
          icon: "warning",
          title: "入住日期錯誤",
          text: "入住日期不可小於今日！",
        });
        this.checkinDate = today;
      }

      if (this.checkoutDate && this.checkoutDate <= this.checkinDate) {
        Swal.fire({
          icon: "warning",
          title: "退房日期錯誤",
          text: "退房日期不可小於或等於入住日期！",
        });
        this.checkoutDate = "";
      }
    },

    // 檢查是否登入
    isLoggedIn() {
      const token = document.cookie
        .split("; ")
        .find((row) => row.startsWith("token="))
        ?.split("=")[1];

      return !!token;
    },

    // 自動查詢房型（日期、人數變更時觸發）
    async autoFetchRooms() {
      if (!this.checkinDate || !this.checkoutDate) return;
      if (new Date(this.checkoutDate) <= new Date(this.checkinDate)) return;

      try {
        const requestPayload = {
          CheckInDate: this.checkinDate,
          CheckOutDate: this.checkoutDate,
          RequiredRoomCount: this.roomCount,
          RequiredPeopleCount: this.totalGuestsForAPI,
          ServiceType: this.activeTab === "booking" ? "房間" : "湯屋",
        };

        const availableRooms = await searchAvailableRooms(requestPayload);
        this.rooms = availableRooms;
      } catch (err) {
        console.error("❌ 查詢房型失敗:", err);
      }
    },

    // 回傳該房型對應的套餐資料
    getPackagesForRoom(roomId) {
      const room = this.rooms.find((r) => r.id === roomId);
      return room ? room.packages.filter((p) => p.id !== null) : [];
    },

    // 計算套餐總價
    calculatePackageTotal(room, packageItem) {
      return packageItem.finalPrice * this.stayNights;
    },

    // 房型圖片取得方法
    getRoomImage(room) {
      try {
        if (room.image) {
          return require(`@/assets/picture/Booking/${room.image}`);
        }
        return require("@/assets/picture/Booking/room1.png");
      } catch (e) {
        console.warn(`找不到圖片: ${room.image}，使用預設圖片`);
        return require("@/assets/picture/Booking/room1.png");
      }
    },

    toggleSummary() {
      this.isSummaryExpanded = !this.isSummaryExpanded;
    },
  },

  created() {
    // 頁面建立時執行登入驗證與初始查詢
    if (!this.isLoggedIn()) {
      this.$router.push("/login");
      Swal.fire({
        icon: "info",
        title: "請先登入",
        text: "未登入，將導向登入頁面",
        timer: 2000,
        showConfirmButton: false,
      });
    } else {
      this.autoFetchRooms();
    }
  },

  watch: {
    // 監聽變數變動自動查詢房型
    checkinDate: "autoFetchRooms",
    checkoutDate: "autoFetchRooms",
    roomCount: "autoFetchRooms",
    roomAdultCount: "autoFetchRooms",
    roomChildCount: "autoFetchRooms",
    activeTab: "autoFetchRooms",
  },
};
</script>

<!-- *在此僅匯入CSS檔案，建議使用scoped或module處理 -->
<style src="@/assets/css/booking.css" scoped></style>
